import classNames from 'classnames';

import {SegmentedControlButton} from './segmented-control-button';

import styles from './segmented-control.module.css';

export interface ControlOption<T> {
    value: T;
    id?: string;
    type?: 'small' | 'large';
    title?: string;
    subject?: string;
    content?: string | React.ReactNode;
    isDisabled?: boolean;
    disabledPopoverText?: string | null | undefined;
    /**
     * If true, when a user selects this option, the selector will not move to
     * it. However, the `onChange` and `onClick` callbacks will still be called,
     * so the option will act like "just a button".
     */
    justAButton?: boolean;
    onClick?: () => unknown;
    icon?: React.ComponentType<any>;
    iconColor?: string;
    // This allows us to use old and new icons and be able to size them correctly
    isLegacyIcon?: boolean;
}

export interface Props<T> {
    buttonOptions: ControlOption<T>[];
    selectedValue: T;
    setSelectedValue: (value: T) => void;
    isDisabled?: boolean;
    onChange?: (value: T) => void;
    isAnimated?: boolean;
    hasPadding?: boolean;
    variant?: 'connected' | 'separated';
}

export function SegmentedControl<T extends string | number>(props: Props<T>) {
    const {hasPadding = true, variant = 'connected'} = props;

    return (
        <div
            className={classNames(styles['control-container'], {
                [styles['control-container--disabled']]: props.isDisabled,
                [styles['control-container--no-padding']]: !hasPadding,
                [styles['control-container--connected']]: variant === 'connected',
                [styles['control-container--separated']]: variant === 'separated',
            })}
        >
            {props.buttonOptions.map((option) => (
                <SegmentedControlButton
                    {...props}
                    key={option.value}
                    // @ts-expect-error
                    id={option.id}
                    option={option}
                    isSelected={option.value === props.selectedValue}
                    isDisabled={option.isDisabled || props.isDisabled}
                    disabledPopoverText={option.disabledPopoverText}
                    isAnimated={props.isAnimated}
                />
            ))}
        </div>
    );
}
